import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronRight, Check, RotateCcw, Download, MoreHorizontal } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';

import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useOrgChartState from '@/states/organization.state';
import OrgChartTreeView from '@/components/company/organization/OrgChartTreeView';
import OrgStructureTable, { type OrgStructureTableRef } from '@/components/company/organization/OrgStructureTable';
import {
  flattenTreeToRows,
  rowsToBulkPayload,
  rowsToIOrgChartTree,
  type OrgTableRow,
} from './organization/org-structure-table';
import { useOrgChartInit } from '@/hooks/useOrgChartInit';
import { useGetCompanyData } from './company-data/useCompanyData';
import { useBankDataJabatans } from '@/hooks/useBankDataJabatans';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import TemplateSelectionView from '@/components/company/organization/TemplateSelectionView';
import TemplateSelectionModal from '@/components/company/organization/TemplateSelectionModal';
import { swalQuestionDefault } from '@/components/ui/Swal';
import useTenantState from '@/states/tenant.state';
import { buildIOrgChartTree, hasLegacyOrgChartData } from '@/utils/functions/organization.utils';
import { toast } from 'react-toastify';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import StepTipsPanel from '@/components/company/StepTipsPanel';

function LegacyDataBanner({ onReset, isReadOnly }: Readonly<{ onReset: () => void; isReadOnly: boolean }>) {
  return (
    <div className="mx-5 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <p className="text-sm text-amber-800">
        Struktur lama terdeteksi (tanpa metadata divisi dan level). Reset data dibutuhkan untuk menggunakan mode matrix
        4 divisi x 6 level.
      </p>
      {!isReadOnly && (
        <button
          type="button"
          onClick={onReset}
          className="mt-3 rounded-lg border border-amber-500 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100"
        >
          Reset Data Lama
        </button>
      )}
    </div>
  );
}

function StructureToolbar({
  isReadOnly,
  onReset,
  onExportWord,
  onExportPDF,
}: Readonly<{ isReadOnly: boolean; onReset: () => void; onExportWord: () => void; onExportPDF: () => void }>) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  return (
    <div className="hidden md:flex items-center justify-between p-5">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Struktur</h3>
        {!isReadOnly && (
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
            title="Reset Struktur Organisasi"
          >
            <RotateCcw size={16} />
            <span>reset struktur</span>
          </button>
        )}
      </div>

      <div className="relative" ref={exportMenuRef}>
        <button
          type="button"
          onClick={() => setShowExportMenu(v => !v)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors"
          style={{ background: '#FD6118' }}
        >
          <Download size={14} />
          Ekspor
          <MoreHorizontal size={14} />
        </button>
        {showExportMenu && (
          <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            <button
              type="button"
              onClick={() => {
                onExportWord();
                setShowExportMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Download Word (.docx)
            </button>
            <button
              type="button"
              onClick={() => {
                onExportPDF();
                setShowExportMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Download PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileStructureHeader({ isReadOnly, onReset }: Readonly<{ isReadOnly: boolean; onReset: () => void }>) {
  return (
    <div className="md:hidden flex items-center justify-between mb-2">
      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Struktur</h3>
      {!isReadOnly && (
        <button
          onClick={onReset}
          className="flex items-center gap-2 px-3 py-2 mt-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
          title="Reset Struktur Organisasi"
        >
          <RotateCcw size={16} />
          <span>reset struktur</span>
        </button>
      )}
    </div>
  );
}

const resetStructureWithConfirmation = async (refreshTree: () => Promise<void>) => {
  const result = await swalQuestionDefault.fire({
    title: 'Konfirmasi Reset',
    text: 'Apakah Anda yakin ingin membuat ulang struktur? Struktur organisasi yang ada saat ini akan DIHAPUS permanen. Data di dalam Key Activities, Job Description, Report, SOP dan Train the trainer akan kosong kembali.',
    confirmButtonText: 'Ya, Reset',
    cancelButtonText: 'Batal',
  });

  if (!result.isConfirmed) return;

  const tenantId = useTenantState.getState().currentTenant?.id;
  if (!tenantId) {
    toast.error('Tenant tidak ditemukan');
    return;
  }
  await useOrgChartState.getState().resetOrgChart(tenantId);
  await refreshTree();
};

export default function OrganizationStructure() {
  const navigate = useNavigate();
  const { tenant_id } = useParams();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const { treeData, isLoading, hasData, createInitialNode, refreshTree } = useOrgChartInit();
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [showPanel, setShowPanel] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [liveRows, setLiveRows] = useState<OrgTableRow[]>([]);

  const tableRef = useRef<OrgStructureTableRef>(null);

  const isReadOnly = useIsReadOnly();
  const { currentTenant } = useTenantState();
  const { bulkUpsertOrgChart } = useOrgChartState();

  const { data: companyData } = useGetCompanyData(currentTenant?.id);
  const { data: jabatanOptions = [], isLoading: jabatanLoading } = useBankDataJabatans(
    companyData?.industry,
    companyData?.business_category,
  );

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const orgStep = companyProfileSteps.find(step =>
    ['organization structure', 'organization'].includes(step.step_name.trim().toLowerCase()),
  );

  const videoLink = orgStep?.video_link;
  const tips = orgStep?.tips;
  const nodes = useMemo(() => (Array.isArray(treeData) ? treeData : []), [treeData]);
  const initialRows = useMemo(() => flattenTreeToRows(buildIOrgChartTree(nodes)), [nodes]);
  const tableKey = useMemo(() => `${nodes.length}:${nodes.map(n => n.id).join('-')}`, [nodes]);
  const legacyDataExists = hasLegacyOrgChartData(nodes);

  // Seed liveRows from server data when it first loads (or after save+refresh).
  // Depend on tableKey only — it is a stable string that changes when the server node list
  // changes. Including initialRows (a fresh array every render) would defeat that guard and
  // loop setLiveRows → re-render → new initialRows → setLiveRows … indefinitely.
  useEffect(() => {
    setLiveRows(initialRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  const liveNodes = useMemo(() => rowsToIOrgChartTree(liveRows), [liveRows]);

  const handleResetStructure = async () => resetStructureWithConfirmation(refreshTree);

  const handleBulkSave = async (rows: OrgTableRow[]) => {
    if (!currentTenant?.id) {
      toast.error('Tenant tidak ditemukan');
      return;
    }
    setIsSaving(true);
    try {
      await bulkUpsertOrgChart(currentTenant.id, rowsToBulkPayload(rows));
      await refetchCompletionStatus();
      await refreshTree();
      toast.success('Struktur organisasi berhasil disimpan');
      navigate(`/${tenant_id || currentTenant.slug || currentTenant.id}/key-activities`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => tableRef.current?.save();

  const handleExportWord = async () => {
    if (!currentTenant) return;
    try {
      const url = `/api/v1/compro/org-chart/tenant/${currentTenant.id}/tree/download?format=word`;
      const response = await axiosInstance.get(url, {
        baseURL: env.apiTasklistBaseUrl,
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `organization-structure-${currentTenant.slug}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast.error('Gagal mengunduh file Word');
    }
  };

  const handleExportPDF = async () => {
    if (!currentTenant) return;
    try {
      const url = `/api/v1/compro/org-chart/tenant/${currentTenant.id}/tree/download?format=pdf`;
      const response = await axiosInstance.get(url, {
        baseURL: env.apiTasklistBaseUrl,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `organization-structure-${currentTenant.slug}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast.error('Gagal mengunduh file PDF');
    }
  };

  const handleSelectTemplate = (template: { id: number }) => {
    setSelectedTemplateId(template.id);
    setTemplateModalOpen(true);
  };

  // Shared header buttons
  const headerButtons = (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => setShowPanel(v => !v)}
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
        {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
      </button>

      {!isReadOnly && hasData !== false && (
        <button
          type="button"
          onClick={handleSaveClick}
          disabled={isSaving}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
          style={{ background: '#FD6118' }}
        >
          <Check size={15} />
          {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
        </button>
      )}
    </div>
  );

  // Empty state
  if (hasData === false && !isLoading) {
    return (
      <>
        <section className="w-full pb-5">
          <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
            <div className="flex flex-col">
              <h1 className="font-semibold text-lg">Organization Structure</h1>
              <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
                Tentukan hierarki struktur organisasi perusahaan.
              </p>
            </div>
            {headerButtons}
          </div>

          <div className="flex flex-col xl:flex-row gap-6 p-5">
            <div className="flex-1 min-w-0">
              <TemplateSelectionView
                onSelectTemplate={handleSelectTemplate}
                onCreateNew={createInitialNode}
                hideTemplates
              />
            </div>
            {showPanel && (
              <div className="w-full xl:w-80 shrink-0 min-w-0">
                <StepTipsPanel tips={tips} videoLink={videoLink} />
              </div>
            )}
          </div>
        </section>

        {currentTenant && (
          <TemplateSelectionModal
            open={templateModalOpen}
            onClose={() => setTemplateModalOpen(false)}
            onSuccess={refreshTree}
            tenantId={currentTenant.id}
            initialTemplateId={selectedTemplateId}
          />
        )}
      </>
    );
  }

  return (
    <>
      <section className="w-full pb-5">
        <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
          <div className="flex flex-col">
            <h1 className="font-semibold text-lg">Organization Structure</h1>
            <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
              Merancang struktur organisasi dari Staff, Koordinator, Supervisor, Manager, General Manager, hingga
              Direktur.
            </p>
          </div>
          {headerButtons}
        </div>

        {legacyDataExists && <LegacyDataBanner onReset={handleResetStructure} isReadOnly={isReadOnly} />}

        <StructureToolbar
          isReadOnly={isReadOnly}
          onReset={handleResetStructure}
          onExportWord={handleExportWord}
          onExportPDF={handleExportPDF}
        />

        <div className="flex flex-col xl:flex-row gap-6 px-5">
          <div className="flex-1 min-w-0">
            <div className="md:hidden w-full mb-2">
              <MobileStructureHeader isReadOnly={isReadOnly} onReset={handleResetStructure} />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
              <OrgStructureTable
                ref={tableRef}
                key={tableKey}
                initialRows={initialRows}
                onSave={handleBulkSave}
                onChange={setLiveRows}
                disabled={isReadOnly}
                jabatanOptions={jabatanOptions}
                jabatanOptionsLoading={jabatanLoading}
              />
            </div>

            {/* Chart below table */}
            <div className="mt-6">
              <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-4" />
              <h3 className="text-base font-semibold text-blue-600 dark:text-blue-400 mb-4">
                Bagan Struktur Organisasi
              </h3>
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
                <OrgChartTreeView isLoading={isLoading} nodes={liveNodes} />
              </div>
            </div>
          </div>

          {showPanel && (
            <div className="w-full xl:w-80 shrink-0 min-w-0">
              <StepTipsPanel tips={tips} videoLink={videoLink} />
            </div>
          )}
        </div>
      </section>

      {currentTenant && (
        <TemplateSelectionModal
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          onSuccess={refreshTree}
          tenantId={currentTenant.id}
          initialTemplateId={selectedTemplateId}
        />
      )}
    </>
  );
}
